/**
 * Represents a PolicySearch class for searching and highlighting terms in a content container.
 */
class PolicySearch {
    /**
     * Creates an instance of PolicySearch.
     * @param {string} term - The search term.
     * @param {HTMLElement} contentContainer - The container element that holds the content to be searched.
     * @param {HTMLElement} resultsContainer - The container element where the search results will be displayed.
     * @throws {Error} Throws an error if the search term, content container, or results container is not provided.
     */
    constructor(term, contentContainer, resultsContainer) {
        if (!term) {
            throw new Error('A search term must be provided.');
        }
        if (!contentContainer) {
            throw new Error('A content container element must be provided.');
        }
        if (!resultsContainer) {
            throw new Error('A results container element must be provided.');
        }
        this.term = term;
        this.contentContainer = contentContainer;
        this.resultsContainer = resultsContainer;
        this.results = 0;
        this.clearSearchResults();
        this.resultList = document.createElement('ol');
        this.resultsContainer.appendChild(this.resultList);
        this.elementsWithHighlights = null;
        this.anchorIds = new Map();
        this.highlightedSections = new Set();
    }

    /**
     * Performs the search and highlights the matching terms in the content container.
     * @returns {number} The count of occurrences of the search term.
     */
    search() {
        let index = 1;
        this.results = 0;
        this.anchorIds.clear();
        this.highlightedSections.clear();
        this.traverseAndHighlight(this.contentContainer);
        this.elementsWithHighlights = this.contentContainer.querySelectorAll('.highlight');
        this.elementsWithHighlights.forEach((element) => {
            const parent = this.findClosestParentWithClass(element, 'js-policy-search-group');
            if (parent && !this.highlightedSections.has(parent)) {
                const anchorId = `result-group-${index}`;
                const anchor = this.createAnchor(anchorId);
                parent.parentNode.insertBefore(anchor, parent);
                this.anchorIds.set(parent, anchorId);
                this.highlightedSections.add(parent);
                index++;
            }
        });
        this.generateListItems();

        // Remove empty spans and whitespace-only text nodes that are the first child of js-policy-search-group divs - this was added to resolve an issue with css flex displaying incorrectly.
        const policyElements = this.contentContainer.querySelectorAll('.js-policy-search-group');
        policyElements.forEach((element) => {
            const firstChild = element.firstChild;
            if (firstChild && (firstChild.nodeName === 'SPAN' || firstChild.nodeType === Node.TEXT_NODE) && firstChild.textContent.trim() === '') {
                element.removeChild(firstChild);
            }
        });
        this.addSummary();
        return this.results;
    }

    /**
     * Generates the list items for the search results.
     */
    generateListItems() {
        const uniqueSentencesMap = new Map(); // Map to store unique sentences and their corresponding parents
        this.elementsWithHighlights.forEach((element) => {
            const parent = this.findClosestParentWithClass(element, 'js-policy-search-group');
            if (parent) {
                const sentences = this.findParentLiOrSentence(element); // Call findParentLiOrSentence here
                sentences.forEach((sentence) => {
                    // Store the sentence along with its parent in the map
                    if (!uniqueSentencesMap.has(sentence)) {
                        uniqueSentencesMap.set(sentence, parent);
                    }
                });
            }
        });
        uniqueSentencesMap.forEach((parent, sentence) => {
            const anchorId = this.anchorIds.get(parent);
            const listItem = this.createListItem(sentence, anchorId);
            this.resultList.appendChild(listItem);
        });
    }

    /**
     * Creates an anchor element with the specified ID.
     * @param {string} id - The ID of the anchor element.
     * @returns {HTMLAnchorElement} The created anchor element.
     */
    createAnchor(id) {
        const anchor = document.createElement('a');
        anchor.className = 'anchor';
        anchor.id = id;
        return anchor;
    }

    /**
     * Creates a list item element with the specified content, ID, and index.
     * @param {string} content - The content of the list item.
     * @param {string} id - The ID of the list item.
     * @param {number} index - The index of the search result.
     * @returns {HTMLLIElement} The created list item element.
     */
    createListItem(content, id) {
        const listItem = document.createElement('li');
        const samePageLink = document.createElement('a');
        const url = window.location.href.split('#')[0];
        samePageLink.href = `${url}#${id}`;
        samePageLink.textContent = content; // Use the sentence as content
        listItem.appendChild(samePageLink);
        return listItem;
    }

    /**
     * Finds the parent list item or sentence for the given element.
     * @param {HTMLElement} element - The element to search for.
     * @returns {string[]} An array of parent list items or sentences.
     */
    findParentLiOrSentence(element) {
        // Check if the search result is inside an li tag
        let parentLi = this.findClosestParentWithTagName(element, 'li');
        if (parentLi) {
            return [parentLi.textContent.trim()];
        }

        // Check if the search result is part of a sentence within its parent element
        let parentElement = element.parentElement;
        const sentences = [];

        // Traverse up the DOM tree until we find an element with the class js-policy-search-group
        while (parentElement && !parentElement.classList.contains('js-policy-search-group')) {
            const displayStyle = window.getComputedStyle(parentElement).display;
            if (displayStyle === 'block' || displayStyle === 'list-item') {
                // Now, let's extract the sentences containing the search result
                const parentSentences = this.extractSentencesContainingElement(parentElement, element);
                sentences.push(...parentSentences);
            }
            parentElement = parentElement.parentElement;
        }

        // If no sentences found, return the entire text content of the parent element
        if (sentences.length === 0) {
            sentences.push(element.parentElement.textContent.trim());
        }

        return sentences;
    }


    /**
     * Extracts the unique sentences containing the search term within the given parent element.
     * @param {HTMLElement} parentElement - The parent element containing the text content.
     * @param {HTMLElement} element - The element to search for within the parent element.
     * @returns {string[]} An array of unique sentences containing the search term.
     */
    extractSentencesContainingElement(parentElement, element) {
        const textContent = parentElement.textContent.trim();
        const searchResultText = element.textContent.trim();

        // Split the text content into sentences
        const sentences = textContent.split(/(?<=[.!?])\s+(?=\p{Lu})/u);

        const sentencesToReturn = [];

        // Find and collect unique sentences containing the search result text
        for (const sentence of sentences) {
            // Check if the search result text is found within the sentence
            if (sentence.includes(searchResultText)) {
                // Further validate by checking if the search term itself is present in the sentence
                const regex = new RegExp(`\\b${this.term}\\b`, 'gi');
                if (regex.test(sentence)) {
                    sentencesToReturn.push(sentence);
                }
            }
        }

        // Return an array of unique sentences containing the search term
        return [...new Set(sentencesToReturn)];
    }

    /**
     * Traverses the DOM tree and highlights the matching terms in the text nodes.
     * @param {Node} node - The current node to traverse.
     */
    traverseAndHighlight(node) {
        if (node.nodeType === Node.TEXT_NODE) {
            const regex = new RegExp(this.term, 'gi');
            const highlightedText = node.nodeValue.replace(regex, (match) => {
                this.results++;
                return `<span class="highlight">${match}</span>`;
            });
            const wrapper = document.createElement('span');
            wrapper.innerHTML = highlightedText;
            node.parentNode.replaceChild(wrapper, node);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const childNodes = Array.from(node.childNodes);
            childNodes.forEach(childNode => this.traverseAndHighlight(childNode));
        }
    }

    /**
     * Finds the closest parent element with the specified class name.
     * @param {HTMLElement} element - The element to start the search from.
     * @param {string} className - The class name to search for.
     * @returns {HTMLElement|null} The closest parent element with the specified class name, or null if not found.
     */
    findClosestParentWithClass(element, className) {
        let parent = element.parentNode;
        while (parent) {
            if (parent.classList.contains(className)) {
                return parent;
            }
            parent = parent.parentNode;
        }
        return null;
    }

    /**
     * Finds the closest parent element with the specified tag name.
     * @param {HTMLElement} element - The element to start the search from.
     * @param {string} tagName - The tag name to search for.
     * @returns {HTMLElement|null} The closest parent element with the specified tag name, or null if not found.
     */
    findClosestParentWithTagName(element, tagName) {
        let parent = element.parentNode;
        while (parent) {
            if (parent.tagName && parent.tagName.toLowerCase() === tagName.toLowerCase()) {
                return parent;
            }
            parent = parent.parentNode;
        }
        return null;
    }

    /**
     * Adds a summary before the search results list.
     */
    addSummary() {
        const totalResults = this.resultList.querySelectorAll('li').length;
        let summaryText = '';
        if (totalResults === 0) {
            summaryText = 'No matches found.';
        } else if (totalResults === 1) {
            summaryText = '1 match found: ';
        } else {
            summaryText = `${totalResults} matches found:`;
        }

        const summaryElement = document.createElement('p');
        const strongElement = document.createElement('strong');
        strongElement.textContent = summaryText;
        summaryElement.appendChild(strongElement);

        this.resultsContainer.insertBefore(summaryElement, this.resultList);
    }

    /**
     * Clears the search results.
     */
    clearSearchResults() {
        this.resultsContainer.innerHTML = '';
        this.clearHighlights();
    }

    /**
     * Clears all highlights from the content container.
     */
    clearHighlights() {
        const highlightedElements = this.contentContainer.querySelectorAll('.highlight');
        highlightedElements.forEach((element) => {
            const text = document.createTextNode(element.textContent);
            element.parentNode.replaceChild(text, element);
        });
    }
}
